import { h, render, Fragment, Component, options } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  getKustomer,
  getKustomerCore,
  handleFirstTab,
  handleMouseClick,
} from 'widget_main/globals/helpers';

import * as helpers from 'widget_main/app/helpers';
import { AppProps } from 'widget_main/app/types';
import { CoreStoreRevisionContext } from 'widget_main/store';

import App from './app';
import { initSentry } from '../../globals/sentry';
import { getMessagesForLanguage, setDocumentLang } from '../../i18n/helpers';
import { NormalizedStartParameters } from '../widget_api/types';
import { parseStartParameters } from '../widget_api/helpers';
import { isInAppBrowser, bootstrapEntryPoint } from '../../helpers/browser';
import { resolveChatAvailability } from './resolveChatAvailability';
import { syncHistoryFilterStoreFromStartParameters } from './historyFilterStore';

import './styles/base.scss';

const kustomerCore = getKustomerCore();

/**
 * Parent `window.Kustomer.startParameters` can change after the iframe bundle loads
 * (e.g. live preview toggles) while the widget module's initial snapshot would stay stale.
 * Always prefer the latest parent value for init and for start-parameter-driven UI.
 */
const capturedStartParameters = getKustomer()?.startParameters;

const resolveStartParameters = (): NormalizedStartParameters =>
  getKustomer()?.startParameters ??
  capturedStartParameters ??
  parseStartParameters();

const Root = () => {
  const [chatSettings, setChatSettings] = useState<AppProps['chatSettings']>();
  const [intlDefinitions, setIntlDefinitions] =
    useState<AppProps['intlDefinitions']>();
  const [chatAvailability, setChatAvailability] =
    useState<AppProps['chatAvailability']>();
  const [coreStoreRevision, setCoreStoreRevision] = useState(0);

  useEffect(() => {
    window.addEventListener('keydown', handleFirstTab);

    return () => {
      window.removeEventListener('keydown', handleFirstTab);
      window.removeEventListener('mousedown', handleMouseClick);
    };
  }, []);

  const loadSettings = () => {
    const startParameters = resolveStartParameters();
    syncHistoryFilterStoreFromStartParameters(startParameters);
    // Even though kustomerCore.init returns a Promise, it is possible for it to resolve before pubnub finishes initiating.
    // That is why we wrap it in another Promise that will only resolve once the callback is called.
    return new Promise<void>((resolve) => {
      kustomerCore.init(
        {
          scheduleId: startParameters.scheduleId,
          brandId: startParameters.brandId,
          lang: startParameters.lang,
          assistantId: startParameters.assistantId,
          testAutomationId: startParameters.testAutomationId,
          enableUrlUnfurling: startParameters.enableUrlUnfurling,
          enableHistoryStatusFilter: startParameters.enableHistoryStatusFilter,
          historyStatusFilter: startParameters.historyStatusFilter,
          customHistoryFilter: startParameters.customHistoryFilter,
        },
        (response, error) => {
          if (error) return;
          const { settings } = response;

          if (settings.sentry?.web && !isInAppBrowser()) {
            // can we get org info here? maybe we should decode the api key? or is there more info on settings?
            initSentry(settings.sentry.web);
          }

          setChatSettings(settings);

          const chatAvailability = resolveChatAvailability({
            availability: kustomerCore.isChatAvailable().availability,
            offhoursDisplay: settings.offhoursDisplay,
            startParameters,
          });

          setChatAvailability(chatAvailability);

          helpers.setThemeVariables(settings);

          setCoreStoreRevision((n) => n + 1);

          resolve();
        },
      );
    });
  };

  useEffect(() => {
    // init on first render
    loadSettings();
    setDocumentLang();
    getMessagesForLanguage().then((langModule) => {
      setIntlDefinitions(langModule?.default);
      setDocumentLang();
    });
  }, []);

  if (!chatSettings) {
    return null;
  }

  const startParameters = resolveStartParameters();

  return (
    <CoreStoreRevisionContext.Provider value={coreStoreRevision}>
      <App
        intlDefinitions={intlDefinitions}
        chatSettings={chatSettings}
        chatAvailability={chatAvailability}
        hideHistory={startParameters?.hideHistory}
        hideChatIcon={!!startParameters?.hideChatIcon}
        hideNewConversationButtonSettings={{
          onChatHistory: !!startParameters?.hideNewConversationButtonOnHistory,
          onConversationEnd: !!startParameters?.hideNewConversationButtonOnEnd,
        }}
        reloadSettings={loadSettings}
      />
    </CoreStoreRevisionContext.Provider>
  );
};

bootstrapEntryPoint(() => {
  // preact-devtools claims to work in iframes, but that doesn't seem to work?
  // Force install it to the top frame instead.
  try {
    // eslint-disable-next-line no-underscore-dangle
    const devtools = (window.top as any)?.__PREACT_DEVTOOLS__;
    if (devtools) {
      devtools.attachPreact('10.29.1', options, {
        Fragment,
        Component,
      });
    }
  } catch {}
});

render(<Root />, document.getElementById('root') as HTMLElement);
